import { CloseCircleOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ADD_TO_REVIEWER_LIST,
  ErrorResponse,
  GET_ALL_USERS_BY_FILTER,
  GET_IDEA_REVIEWER_BY_ID,
  GET_REVIEWER_TAGS,
  IdeaReviewerList,
  ReviewerTag,
  UPDATE_REVIEWER_LIST_ITEM,
  User,
} from '@frontend/data-access';
import { UserAvatar } from '@frontend/shared/ui';
import { App, Button, Divider, Form, Input, Select, Spin } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import CreateReviewerTag from './create-reviewer-tag';

const WAIT_INTERVAL = 500;

interface FormValues {
  userId: number;
  role: string;
  tagId: number;
  description: string;
}

function AddFromPlatform({
  channelId,
  id,
  onFinish,
}: {
  channelId: string;
  id?: string;
  onFinish: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [getData, { data, loading }] = useLazyQuery<{
    getIdeaReviewerItem: IdeaReviewerList;
  }>(GET_IDEA_REVIEWER_BY_ID);
  const { data: reviewerTags, loading: reviewerTagLoading } = useQuery<{
    listReviewerTag: ReviewerTag[];
  }>(GET_REVIEWER_TAGS, {
    variables: { channelId },
  });
  const [searchUser, { data: userData, loading: userLoading }] = useLazyQuery<{
    publicUsers: User[];
  }>(GET_ALL_USERS_BY_FILTER);

  const requiredRules = [
    {
      required: true,
      message: t('required_field'),
    },
  ];

  const handleCompleted = () => {
    form.resetFields();
    onFinish();
  };
  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };
  const [addToReviewerList, { loading: createLoading }] = useMutation(
    ADD_TO_REVIEWER_LIST,
    { onCompleted: handleCompleted, onError: handleError }
  );
  const [updateReviewerListItem, { loading: updateLoading }] = useMutation(
    UPDATE_REVIEWER_LIST_ITEM,
    { onCompleted: handleCompleted, onError: handleError }
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim());
  };

  const handleOnFinish = (values: FormValues) => {
    if (id) {
      updateReviewerListItem({
        variables: {
          input: {
            ...values,
            userId: undefined,
            channelId,
            id,
          },
        },
      });
    } else {
      addToReviewerList({
        variables: {
          input: {
            ...values,
            channelId,
          },
        },
      });
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      searchUser({
        variables: {
          searchBy: searchTerm,
          filterBy: {
            roles: ['ADMIN', 'INTRAPRENEUR', 'CHALLENGE_MANAGER'],
            field: 'email',
          },
          limit: 100,
        },
      });
    }, WAIT_INTERVAL);

    return () => clearTimeout(delayDebounceFn);
  }, [searchUser, searchTerm]);

  useEffect(() => {
    if (channelId && id) {
      getData({
        variables: {
          channelId,
          id,
        },
      });
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, id, getData]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data.getIdeaReviewerItem,
        userId: data.getIdeaReviewerItem.user?.id,
        tagId: data.getIdeaReviewerItem.reviewerTag?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spin />
      </div>
    );
  }

  return (
    <div className="">
      <Form form={form} layout="vertical" onFinish={handleOnFinish}>
        <Form.Item name="userId" label={t('user')} rules={requiredRules}>
          <Select
            showSearch
            allowClear
            placeholder={t('search_by_email')}
            loading={userLoading}
            className="w-full"
            disabled={!!id}
            notFoundContent={
              userLoading ? (
                <div className="text-center">
                  <Spin size="small" />
                </div>
              ) : null
            }
            filterOption={(input, option) =>
              ((option?.label as string) ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onSearch={handleSearch}
          >
            {userData?.publicUsers.map((item) => (
              <Select.Option key={item.id} value={item.id} label={item.email}>
                <div className="flex items-center gap-2">
                  <UserAvatar size={24} user={item} />
                  {item.email}
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="role" label={t('title')} rules={requiredRules}>
          <Input placeholder={t('enter_user_title')} />
        </Form.Item>
        <Form.Item name="tagId" label={t('tag')} rules={requiredRules}>
          <Select
            placeholder={t('select_tag')}
            loading={reviewerTagLoading}
            options={reviewerTags?.listReviewerTag.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <CreateReviewerTag channelId={channelId} />
              </>
            )}
          />
        </Form.Item>
        <Form.Item name="description" label={t('note')} rules={requiredRules}>
          <Input.TextArea
            rows={4}
            placeholder={t('enter_user_identifying_note')}
          />
        </Form.Item>
        <div className="flex items-center gap-2 justify-end">
          <Button
            shape="round"
            type="primary"
            htmlType="submit"
            loading={createLoading || updateLoading}
          >
            {id ? t('update') : t('add')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default AddFromPlatform;
